import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import marketingService, { CampaignFilters, CreateCampaignDto } from '../../services/marketingService';
import { Campaign, CampaignStats } from '../../types';

const errorMessage = (error:unknown):string =>
  (error as { response?:{ data?:{ message?:string } } }).response?.data?.message
  ?? (error instanceof Error ? error.message : 'Request failed');

interface MarketingState {
  list:     Campaign[];
  stats:    CampaignStats | null;
  loading:  boolean;
  error:    string | null;
}

const initialState: MarketingState = { list: [], stats: null, loading: false, error: null };

const upsert = (list: Campaign[], c: Campaign) => {
  const i = list.findIndex(x => x._id === c._id);
  if (i !== -1) list[i] = c; else list.unshift(c);
};

export const fetchCampaignStats  = createAsyncThunk('marketing/stats',   async (_, { rejectWithValue }) => { try { return await marketingService.getStats(); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const fetchCampaigns      = createAsyncThunk('marketing/fetchAll', async (f: CampaignFilters, { rejectWithValue }) => { try { return await marketingService.getAll(f); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const createCampaign      = createAsyncThunk('marketing/create',   async (d: CreateCampaignDto, { rejectWithValue }) => { try { return await marketingService.create(d); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const updateCampaign      = createAsyncThunk('marketing/update',   async ({ id, data }: { id: string; data: Partial<CreateCampaignDto> }, { rejectWithValue }) => { try { return await marketingService.update(id, data); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const sendCampaign        = createAsyncThunk('marketing/send',     async (id: string, { rejectWithValue }) => { try { return await marketingService.send(id); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const scheduleCampaign    = createAsyncThunk('marketing/schedule', async ({ id, scheduledAt }: { id: string; scheduledAt: string }, { rejectWithValue }) => { try { return await marketingService.schedule(id, scheduledAt); } catch (e: unknown) { return rejectWithValue((e as Error).message); } });
export const deleteCampaign      = createAsyncThunk('marketing/delete',   async (id: string, { rejectWithValue }) => { try { await marketingService.delete(id); return id; } catch (e: unknown) { return rejectWithValue((e as Error).message); } });

const marketingSlice = createSlice({
  name: 'marketing', initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaignStats.fulfilled,  (s, a) => { s.stats = a.payload.data ?? null; })
      .addCase(fetchCampaigns.pending,         (s)    => { s.loading = true; s.error = null; })
      .addCase(fetchCampaigns.fulfilled,       (s, a) => { s.loading = false; s.list = a.payload.data ?? []; })
      .addCase(fetchCampaigns.rejected,        (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(createCampaign.fulfilled,       (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(updateCampaign.fulfilled,       (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(sendCampaign.fulfilled,         (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(scheduleCampaign.fulfilled,     (s, a) => { if (a.payload.data) upsert(s.list, a.payload.data); })
      .addCase(deleteCampaign.fulfilled,       (s, a) => { s.list = s.list.filter(c => c._id !== a.payload); });
  },
});

export const { clearError } = marketingSlice.actions;
export default marketingSlice.reducer;
